import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { apiUrl, type UnitScale, unitLabel } from '../App';
import { authedFetch } from '../supabaseClient';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ChartPoint {
  period: string;
  label: string;
  value: number | null;
}

type FormatType = 'dollar' | 'pct' | 'count' | 'ratio';

interface StackedCategory {
  name: string;
  data: Record<string, number>;
}

interface ChartConfig {
  title: string;
  format: FormatType;
  section: string;
  extract: (pnl: any, cust: any, cac: any) => ChartPoint[];
  extractStacked?: (pnl: any) => StackedCategory[] | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

let _scale = 1000;

function fmtDollar(v: number): string {
  const abs = Math.abs(v) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return v < 0 ? `($${str})` : `$${str}`;
}
function fmtPct(v: number): string { return `${Math.round(v)}%`; }
function fmtCount(v: number): string { return Math.round(v).toLocaleString(); }
function fmtRatio(v: number): string { return v.toFixed(2) + 'x'; }

const FORMATTERS: Record<FormatType, (v: number) => string> = {
  dollar: fmtDollar, pct: fmtPct, count: fmtCount, ratio: fmtRatio,
};

function round2(v: number): number { return Math.round(v * 100) / 100; }

// Strip leading nulls so charts don't show blank bars at the start
function stripLeadingNulls(data: ChartPoint[]): ChartPoint[] {
  const firstIdx = data.findIndex(d => d.value != null);
  return firstIdx <= 0 ? data : data.slice(firstIdx);
}

// Skip first customer period (catch-all of pre-existing customers)
function skipFirstCustPeriod(data: ChartPoint[]): ChartPoint[] {
  return data.length > 1 ? data.slice(1) : data;
}

// ── Trendline ────────────────────────────────────────────────────────────────

function computeTrend(data: ChartPoint[]): (number | null)[] {
  const indexed: { i: number; v: number }[] = [];
  data.forEach((d, i) => { if (d.value != null) indexed.push({ i, v: d.value }); });
  if (indexed.length < 2) return data.map(() => null);
  const n = indexed.length;
  const sumX = indexed.reduce((s, d) => s + d.i, 0);
  const sumY = indexed.reduce((s, d) => s + d.v, 0);
  const sumXY = indexed.reduce((s, d) => s + d.i * d.v, 0);
  const sumXX = indexed.reduce((s, d) => s + d.i * d.i, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  const allNonNeg = indexed.every(d => d.v >= 0);
  return data.map((_, i) => {
    if (i >= indexed[0].i && i <= indexed[indexed.length - 1].i) {
      const v = round2(intercept + slope * i);
      return allNonNeg && v !== null && v < 0 ? 0 : v;
    }
    return null;
  });
}

// ── Data extraction helpers ──────────────────────────────────────────────────

function pnlSeries(pnl: any, key: string): ChartPoint[] {
  if (!pnl) return [];
  const periods: string[] = pnl.periods || [];
  const data: Record<string, number> = pnl[key] || {};
  return periods.map(p => ({ period: p, label: formatPeriod(p), value: data[p] ?? null }));
}

function rrFromSections(pnl: any, sectionKey: string): ChartPoint[] {
  if (!pnl?.operating_metrics?.[sectionKey]?.revenue_sections) return [];
  const sections = pnl.operating_metrics[sectionKey].revenue_sections;
  const periods: string[] = pnl.periods || [];
  const section = sections[sections.length - 1];
  if (!section) return [];
  return periods.map(p => ({ period: p, label: formatPeriod(p), value: section.values[p] ?? null }));
}

function metricFromDict(periods: string[], dict: Record<string, number | null> | undefined): ChartPoint[] {
  if (!dict) return [];
  return periods.map(p => ({ period: p, label: formatPeriod(p), value: dict[p] ?? null }));
}

function custBuildField(cust: any, field: string): ChartPoint[] {
  if (!cust) return [];
  const periods: string[] = cust.period_cols || [];
  const build = cust.customer_build || {};
  return periods.map(p => {
    const b = build[p];
    return { period: p, label: formatPeriod(p), value: b ? (b[field] ?? null) : null };
  });
}

function mrrBuildField(cust: any, field: string): ChartPoint[] {
  if (!cust) return [];
  const periods: string[] = cust.period_cols || [];
  const build = cust.mrr_build || {};
  return periods.map(p => {
    const b = build[p];
    return { period: p, label: formatPeriod(p), value: b ? (b[field] ?? null) : null };
  });
}

// ── Stacked category extraction for Run-Rate Revenue ────────────────────────

function extractRRCategories(pnl: any): StackedCategory[] | null {
  if (!pnl?.operating_metrics?.run_rate_revenue?.revenue_sections) return null;
  const sections = pnl.operating_metrics.run_rate_revenue.revenue_sections;
  // Use the last section (Revenue or Net Revenue)
  const section = sections[sections.length - 1];
  if (!section?.categories || section.categories.length <= 1) return null;
  return section.categories.map((cat: any) => ({
    name: cat.category,
    data: cat.values || {},
  }));
}

// Stacked bar color palette
const STACK_COLORS = [
  '#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#34d399',
  '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16',
];

// ── Chart configurations (ordered by category) ──────────────────────────────

function buildChartConfigs(pnlData: any): ChartConfig[] {
  const volLabel = pnlData?.operating_metrics?.volume_metrics?.volume_label || 'Volume';
  return [
    // ═══ Volume Metrics (conditional — only shown when GMV/TPV/AUM data exists) ═══
    { title: `Run-Rate ${volLabel}`, format: 'dollar', section: 'Volume Metrics',
      extract: (pnl) => {
        const vm = pnl?.operating_metrics?.volume_metrics;
        if (!vm) return [];
        return metricFromDict(pnl.periods, vm.run_rate);
      }},
    { title: `YoY ${volLabel} Growth`, format: 'pct', section: 'Volume Metrics',
      extract: (pnl) => {
        const vm = pnl?.operating_metrics?.volume_metrics;
        if (!vm) return [];
        return stripLeadingNulls(metricFromDict(pnl.periods, vm.yoy_growth));
      }},
    { title: `TTM YoY ${volLabel} Growth`, format: 'pct', section: 'Volume Metrics',
      extract: (pnl) => {
        const vm = pnl?.operating_metrics?.volume_metrics;
        if (!vm) return [];
        return stripLeadingNulls(metricFromDict(pnl.periods, vm.ttm_yoy_growth));
      }},
    { title: 'Take Rate', format: 'pct', section: 'Volume Metrics',
      extract: (pnl) => {
        const vm = pnl?.operating_metrics?.volume_metrics;
        if (!vm) return [];
        return metricFromDict(pnl.periods, vm.take_rate);
      }},
    { title: 'GP Take Rate', format: 'pct', section: 'Volume Metrics',
      extract: (pnl) => {
        const vm = pnl?.operating_metrics?.volume_metrics;
        if (!vm) return [];
        return metricFromDict(pnl.periods, vm.gp_take_rate);
      }},

    // ═══ Revenue & Growth ═══
    { title: 'Run-Rate Revenue (P&L)', format: 'dollar', section: 'Revenue & Growth',
      extract: (pnl) => rrFromSections(pnl, 'run_rate_revenue'),
      extractStacked: extractRRCategories },
    { title: 'Run-Rate Revenue (Customer Data)', format: 'dollar', section: 'Revenue & Growth',
      extract: (_, cust) => !cust ? [] : metricFromDict(cust.period_cols, cust.summary?.run_rate_revenue) },
    { title: 'Run-Rate Gross Profit', format: 'dollar', section: 'Revenue & Growth',
      extract: (pnl) => {
        if (!pnl) return [];
        const n = pnl.period_type === 'quarterly' ? 4 : 12;
        const periods: string[] = pnl.periods || [];
        const gp: Record<string, number> = pnl.gross_profit || {};
        return periods.map(p => ({ period: p, label: formatPeriod(p), value: gp[p] != null ? gp[p] * n : null }));
      }},
    { title: 'YoY Revenue Growth', format: 'pct', section: 'Revenue & Growth',
      extract: (pnl) => stripLeadingNulls(rrFromSections(pnl, 'yoy_growth')) },
    { title: 'TTM YoY Revenue Growth', format: 'pct', section: 'Revenue & Growth',
      extract: (pnl) => stripLeadingNulls(rrFromSections(pnl, 'ttm_yoy_growth')) },

    // ═══ Profitability ═══
    { title: 'EBITDA', format: 'dollar', section: 'Profitability',
      extract: (pnl) => pnlSeries(pnl, 'ebitda') },
    { title: 'Gross Margin', format: 'pct', section: 'Profitability',
      extract: (pnl) => pnlSeries(pnl, 'gross_margin_pct') },
    { title: 'EBITDA Margin', format: 'pct', section: 'Profitability',
      extract: (pnl) => pnlSeries(pnl, 'ebitda_margin_pct') },
    { title: 'Rule of 40', format: 'pct', section: 'Profitability',
      extract: (pnl) => !pnl?.operating_metrics?.rule_of_40?.yoy ? [] : stripLeadingNulls(metricFromDict(pnl.periods, pnl.operating_metrics.rule_of_40.yoy)) },

    // ═══ Cash ═══
    { title: 'Cash Balance', format: 'dollar', section: 'Cash',
      extract: (pnl) => !pnl?.operating_metrics?.cash_runway?.has_cash ? [] : metricFromDict(pnl.periods, pnl.operating_metrics.cash_runway.cash_balance) },
    { title: 'Monthly Change in Cash', format: 'dollar', section: 'Cash',
      extract: (pnl) => {
        if (!pnl?.operating_metrics?.cash_runway?.has_cash) return [];
        const periods: string[] = pnl.periods;
        const cash = pnl.operating_metrics.cash_runway.cash_balance;
        return periods.map((p, i) => {
          if (i === 0) return { period: p, label: formatPeriod(p), value: null };
          return { period: p, label: formatPeriod(p), value: (cash[p] ?? 0) - (cash[periods[i - 1]] ?? 0) };
        });
      }},
    { title: 'CFO', format: 'dollar', section: 'Cash',
      extract: (pnl) => !pnl?.operating_metrics?.cash_runway?.has_cfo ? [] : metricFromDict(pnl.periods, pnl.operating_metrics.cash_runway.cfo_values) },
    { title: 'Cash Runway (Months)', format: 'count', section: 'Cash',
      extract: (pnl) => !pnl?.operating_metrics?.cash_runway?.has_cash ? [] : stripLeadingNulls(metricFromDict(pnl.periods, pnl.operating_metrics.cash_runway.ebitda)) },

    // ═══ Efficiency ═══
    { title: 'S&M % of Revenue', format: 'pct', section: 'Efficiency',
      extract: (pnl) => {
        const cats = pnl?.operating_metrics?.pct_of_revenue?.categories;
        const sm = cats?.find((c: any) => c.category === 'S&M');
        return sm ? metricFromDict(pnl.periods, sm.values) : [];
      }},
    { title: 'R&D % of Revenue', format: 'pct', section: 'Efficiency',
      extract: (pnl) => {
        const cats = pnl?.operating_metrics?.pct_of_revenue?.categories;
        const rd = cats?.find((c: any) => c.category === 'R&D');
        return rd ? metricFromDict(pnl.periods, rd.values) : [];
      }},
    { title: 'G&A % of Revenue', format: 'pct', section: 'Efficiency',
      extract: (pnl) => {
        const cats = pnl?.operating_metrics?.pct_of_revenue?.categories;
        const ga = cats?.find((c: any) => c.category === 'G&A');
        return ga ? metricFromDict(pnl.periods, ga.values) : [];
      }},
    { title: 'Burn Multiple', format: 'ratio', section: 'Efficiency',
      extract: (pnl) => {
        if (!pnl?.operating_metrics?.capital_efficiency) return [];
        const ce = pnl.operating_metrics.capital_efficiency;
        const periods: string[] = pnl.periods || [];
        return stripLeadingNulls(periods.map(p => ({
          period: p, label: formatPeriod(p),
          value: ce[p]?.burn_multiple_pnl ?? null,
        })));
      }},

    // ═══ Customers ═══
    { title: 'Total Customers', format: 'count', section: 'Customers',
      extract: (_, cust) => custBuildField(cust, 'ending') },
    { title: 'New Customers', format: 'count', section: 'Customers',
      extract: (_, cust) => skipFirstCustPeriod(custBuildField(cust, 'additions')) },
    { title: 'Customer Churn %', format: 'pct', section: 'Customers',
      extract: (_, cust) => {
        if (!cust) return [];
        const periods: string[] = cust.period_cols;
        const build = cust.customer_build || {};
        return skipFirstCustPeriod(periods.map(p => {
          const v = build[p]?.churn_pct;
          return { period: p, label: formatPeriod(p), value: v != null ? -v : null };
        }));
      }},

    // ═══ Retention ═══
    { title: 'NRR', format: 'pct', section: 'Retention',
      extract: (_, cust) => !cust ? [] : stripLeadingNulls(metricFromDict(cust.period_cols, cust.nrr)) },
    { title: 'GRR', format: 'pct', section: 'Retention',
      extract: (_, cust) => !cust ? [] : stripLeadingNulls(metricFromDict(cust.period_cols, cust.grr)) },
    { title: 'Revenue Net Churn %', format: 'pct', section: 'Retention',
      extract: (_, cust) => skipFirstCustPeriod(mrrBuildField(cust, 'net_churn_pct')) },
    { title: 'Revenue Gross Churn %', format: 'pct', section: 'Retention',
      extract: (_, cust) => skipFirstCustPeriod(mrrBuildField(cust, 'gross_churn_pct')) },
    { title: 'SaaS Quick Ratio', format: 'ratio', section: 'Retention',
      extract: (_, cust) => {
        if (!cust) return [];
        const periods: string[] = cust.period_cols;
        return skipFirstCustPeriod(periods.map(p => {
          const b = cust.mrr_build?.[p];
          if (!b) return { period: p, label: formatPeriod(p), value: null };
          const pos = (b.new || 0) + (b.resurrection || 0) + (b.upsell || 0);
          const neg = Math.abs((b.contraction || 0) + (b.churned || 0));
          return { period: p, label: formatPeriod(p), value: neg > 0 ? round2(pos / neg) : null };
        }));
      }},

    // ═══ ACV & Revenue Build ═══
    { title: 'ACV per Customer', format: 'dollar', section: 'ACV & Revenue Build',
      extract: (_, cust) => {
        if (!cust) return [];
        const n = cust.period_type === 'quarterly' ? 4 : 12;
        return cust.period_cols.map((p: string) => {
          const ending = cust.mrr_build?.[p]?.ending ?? 0;
          const count = cust.customer_build?.[p]?.ending ?? 0;
          return { period: p, label: formatPeriod(p), value: count > 0 ? (ending * n) / count : null };
        });
      }},
    { title: 'New ACV per New Customer', format: 'dollar', section: 'ACV & Revenue Build',
      extract: (_, cust) => {
        if (!cust) return [];
        const n = cust.period_type === 'quarterly' ? 4 : 12;
        return skipFirstCustPeriod(cust.period_cols.map((p: string) => {
          const newRev = cust.mrr_build?.[p]?.new ?? 0;
          const newCust = cust.customer_build?.[p]?.additions ?? 0;
          return { period: p, label: formatPeriod(p), value: newCust > 0 ? (newRev * n) / newCust : null };
        }));
      }},
    { title: 'New RRR', format: 'dollar', section: 'ACV & Revenue Build',
      extract: (_, cust) => {
        if (!cust) return [];
        const n = cust.period_type === 'quarterly' ? 4 : 12;
        return skipFirstCustPeriod(cust.period_cols.map((p: string) => ({
          period: p, label: formatPeriod(p), value: (cust.mrr_build?.[p]?.new ?? 0) * n,
        })));
      }},
    { title: 'Change in RRR', format: 'dollar', section: 'ACV & Revenue Build',
      extract: (_, cust) => {
        if (!cust) return [];
        const rr = cust.summary?.run_rate_revenue || {};
        return cust.period_cols.map((p: string, i: number) => {
          if (i === 0) return { period: p, label: formatPeriod(p), value: null };
          return { period: p, label: formatPeriod(p), value: (rr[p] ?? 0) - (rr[cust.period_cols[i - 1]] ?? 0) };
        });
      }},

    // ═══ CAC ═══
    { title: 'CAC', format: 'dollar', section: 'CAC',
      extract: (_, __, cac) => {
        if (!cac) return [];
        return skipFirstCustPeriod((cac.period_cols || []).map((p: string) => ({
          period: p, label: formatPeriod(p), value: cac.cac_table?.monthly_cac?.[p] ?? null,
        })));
      }},
  ];
}

// ── KPI Chart Component ──────────────────────────────────────────────────────

function KpiChart({ title, data, format }: { title: string; data: ChartPoint[]; format: FormatType }) {
  const fmt = FORMATTERS[format];
  const chartData = useMemo(() => {
    const trend = computeTrend(data);
    return data.map((d, i) => ({ name: d.label, value: d.value, trend: trend[i] }));
  }, [data]);

  const lastValid = [...data].reverse().find(d => d.value != null);
  const currentLabel = lastValid ? lastValid.label : '';
  const currentValue = lastValid?.value != null ? fmt(lastValid.value) : '—';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
      <div className="flex items-baseline gap-2 mb-2">
        <h4 className="text-xs font-bold text-gray-400">{title}:</h4>
        <span className="text-white text-sm font-bold">{currentValue}</span>
        <span className="text-white text-xs ml-auto">{currentLabel}</span>
      </div>
      <ResponsiveContainer width="100%" height={235}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 8 }} interval="preserveStartEnd" tickLine={false} />
          <YAxis tickFormatter={(v: any) => fmt(Number(v))} tick={{ fill: '#6b7280', fontSize: 8 }} width={50} />
          <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            formatter={(value: any) => [fmt(Number(value)), '']} />
          <Bar dataKey="value" fill="#3b82f6" radius={[1, 1, 0, 0]} />
          <Line type="monotone" dataKey="trend" stroke="#6b7280" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Stacked KPI Chart (Run-Rate Revenue with category breakdown) ────────────

function StackedKpiChart({
  title,
  data,
  categories,
  format,
}: {
  title: string;
  data: ChartPoint[];
  categories: StackedCategory[];
  format: FormatType;
}) {
  const fmt = FORMATTERS[format];

  const chartData = useMemo(() => {
    return data.map(d => {
      const point: Record<string, any> = { name: d.label };
      categories.forEach(cat => {
        const raw = cat.data[d.period] ?? 0;
        point[cat.name] = raw;
      });
      return point;
    });
  }, [data, categories]);

  // Current total value (last period with non-null data)
  const lastValid = [...data].reverse().find(d => d.value != null);
  const currentLabel = lastValid ? lastValid.label : '';
  const currentValue = lastValid?.value != null ? fmt(lastValid.value) : '—';

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
      <div className="flex items-baseline gap-2 mb-2">
        <h4 className="text-xs font-bold text-gray-400">{title}:</h4>
        <span className="text-white text-sm font-bold">{currentValue}</span>
        <span className="text-white text-xs ml-auto">{currentLabel}</span>
      </div>
      <ResponsiveContainer width="100%" height={235}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 8 }} interval="preserveStartEnd" tickLine={false} />
          <YAxis tickFormatter={(v: any) => fmt(Number(v))} tick={{ fill: '#6b7280', fontSize: 8 }} width={50} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            formatter={(value: any, name: any) => [fmt(Number(value)), String(name)]}
          />
          {categories.map((cat, i) => (
            <Bar
              key={cat.name}
              dataKey={cat.name}
              stackId="revenue"
              fill={STACK_COLORS[i % STACK_COLORS.length]}
              radius={i === categories.length - 1 ? [1, 1, 0, 0] : [0, 0, 0, 0]}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex justify-center flex-wrap gap-2 md:gap-4 mt-1">
        {categories.map((cat, i) => (
          <div key={cat.name} className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: STACK_COLORS[i % STACK_COLORS.length] }} />
            <span className="text-gray-400 text-[10px]">{cat.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function KpiChartsView({ projectId, unitScale }: { projectId: string; unitScale: UnitScale }) {
  _scale = unitScale;
  const [pnlData, setPnlData] = useState<any>(null);
  const [custData, setCustData] = useState<any>(null);
  const [cacData, setCacData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      authedFetch(apiUrl('/api/financials/pnl', projectId)).then(r => r.ok ? r.json() : null).catch(() => null),
      authedFetch(apiUrl('/api/customers/analytics', projectId)).then(r => r.ok ? r.json() : null).catch(() => null),
      authedFetch(apiUrl('/api/cac', projectId)).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([pnl, cust, cac]) => { setPnlData(pnl); setCustData(cust); setCacData(cac); setLoading(false); });
  }, [projectId]);

  const charts = useMemo(() => {
    const configs = buildChartConfigs(pnlData);
    return configs.map(c => ({
      ...c,
      data: c.extract(pnlData, custData, cacData),
      stackedCategories: c.extractStacked ? c.extractStacked(pnlData) : null,
    }))
      .filter(c => c.data.length > 0 && c.data.some(d => d.value != null));
  }, [pnlData, custData, cacData]);

  // Group charts by section, preserving order
  const sections = useMemo(() => {
    const sectionOrder: string[] = [];
    const sectionMap: Record<string, typeof charts> = {};
    for (const c of charts) {
      if (!sectionMap[c.section]) {
        sectionOrder.push(c.section);
        sectionMap[c.section] = [];
      }
      sectionMap[c.section].push(c);
    }
    return sectionOrder.map(s => ({ name: s, charts: sectionMap[s] }));
  }, [charts]);

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;
  if (!pnlData && !custData) return (
    <div className="text-center py-16"><p className="text-gray-500 text-sm">No data available. Please upload financials or customer data.</p></div>
  );

  return (
    <div className="space-y-6">
      <span className="text-gray-500 text-xs font-medium">{unitLabel(_scale as UnitScale)}</span>
      {sections.map(s => (
        <div key={s.name}>
          <h3 className="text-sm font-bold text-white mb-3">{s.name}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.charts.map(c =>
              c.stackedCategories ? (
                <StackedKpiChart
                  key={c.title}
                  title={c.title}
                  data={c.data}
                  categories={c.stackedCategories}
                  format={c.format}
                />
              ) : (
                <KpiChart key={c.title} title={c.title} data={c.data} format={c.format} />
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
